import { useTranslation } from 'react-i18next';
import { FieldError } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import {
  Chip,
  SvgCheckCircle1Bold,
  SvgRemoveCircleBold,
} from '@insights-ltd/design-library/components';

import { styled, SxProps } from '@mui/material';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { SPECIAL_CHARACTER_EXAMPLES } from '@insights-ltd/design-library/utils';
import { determineTagState, ErrorState } from './validationUtils';

const StyledCheckIcon = styled(SvgCheckCircle1Bold)(() => ({
  fill: 'currentcolor',
  maxHeight: '20px',
}));

const StyledCloseIcon = styled(SvgRemoveCircleBold)(() => ({
  fill: 'currentcolor',
  maxHeight: '20px',
}));

const ERROR_ICON_MAP = {
  [ErrorState.INVALID]: <StyledCloseIcon />,
  [ErrorState.VALID]: <StyledCheckIcon />,
  [ErrorState.NEUTRAL]: undefined,
} as const;

const ERROR_COLOR_MAP = {
  [ErrorState.INVALID]: 'red',
  [ErrorState.VALID]: 'darkGreen',
  [ErrorState.NEUTRAL]: 'grey',
} as const;

export type ValidationTagsProps = {
  errors?: FieldError;
  confirmErrors?: FieldError;
  value: string;
  confirmationValue?: string;
  sx?: SxProps;
};

type TagProps = {
  label: string;
  value: string;
  errors?: FieldError;
  errorKey: string;
  dataTestId?: string;
  sx?: SxProps;
};

const StyledChip = styled(Chip)(({ theme }) => ({
  marginRight: theme.spacing(spacingSizeMap.XS),
  marginBottom: theme.spacing(spacingSizeMap.S),
  borderColor: 'white',
  marginLeft: -7,
}));

const ValidationLabel = ({
  label,
  value,
  errors,
  errorKey,
  dataTestId,
  sx,
}: TagProps) => {
  const tagState = determineTagState(
    value,
    errors?.types?.required,
    !errors?.types?.[errorKey],
  );

  return (
    <StyledChip
      sx={{
        marginLeft: tagState === ErrorState.NEUTRAL ? -1.5 : undefined,
        ...sx,
      }}
      label={label}
      color={ERROR_COLOR_MAP[tagState]}
      icon={ERROR_ICON_MAP[tagState]}
      variant="outlined"
      aria-disabled="true"
      data-testid={dataTestId}
    />
  );
};

const ValidationTags = ({
  value,
  confirmationValue,
  errors,
  confirmErrors,
  sx,
}: ValidationTagsProps) => {
  const { t } = useTranslation();
  const specialCharactersString = t(
    'ui.event-management.sign-up.password.validation.special-char',
    { specialCharacters: SPECIAL_CHARACTER_EXAMPLES },
  );
  return (
    <Grid
      container
      sx={{
        flexDirection: 'column',
        alignItems: 'start',
      }}
    >
      {confirmationValue !== undefined && (
        <ValidationLabel
          label={t('ui.event-management.sign-up.password.validation.unique')}
          value={value}
          errors={errors}
          errorKey="unique"
          sx={sx}
          dataTestId="ui.event-management.sign-up.password.validation.unique"
        />
      )}
      <ValidationLabel
        label={t('ui.event-management.sign-up.password.validation.length')}
        value={value}
        errors={errors}
        errorKey="minLength"
        sx={sx}
        dataTestId="ui.event-management.sign-up.password.validation.length"
      />
      <ValidationLabel
        label={t(
          'ui.event-management.sign-up.password.validation.loweruppercase-char',
        )}
        value={value}
        errors={errors}
        errorKey="lowerUppercaseChar"
        sx={sx}
        dataTestId="ui.event-management.sign-up.password.validation.loweruppercase-char"
      />
      <ValidationLabel
        label={specialCharactersString}
        value={value}
        errors={errors}
        errorKey="specialChar"
        sx={sx}
        dataTestId="ui.event-management.sign-up.password.validation.special-char"
      />
      <ValidationLabel
        label={t('ui.event-management.sign-up.password.validation.number')}
        value={value}
        errors={errors}
        errorKey="number"
        sx={sx}
        dataTestId="ui.event-management.sign-up.password.validation.number"
      />
      {confirmationValue !== undefined && (
        <ValidationLabel
          label={t('ui.event-management.sign-up.password.validation.matches')}
          value={confirmationValue}
          errors={confirmErrors}
          errorKey="matches"
          sx={sx}
          dataTestId="ui.event-management.sign-up.password.validation.matches"
        />
      )}
    </Grid>
  );
};

export default ValidationTags;
